import React, { useState, useEffect } from 'react'
import ReportHeader from './components/ReportHeader'
import ReportResults from './components/ReportResults'
import messages from './messages'
import { useIntl } from 'react-intl'

const AccountingToBePaidTasksPage = () => {
    const { formatMessage } = useIntl()
    const [entries, setEntries] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedTaskIds, setSelectedTaskIds] = useState([])
    const [showFullTaskRow, setShowFullTaskRow] = useState(false)
    useEffect(() => {
        handleSearch()
    }, [])

    const handleMarkAsPaid = (toBePaidTaskIds) => {
        handleDownloadCSV()
        if (confirm(formatMessage(messages.confirmMarkAsPaidPrompt))) {
            setLoading(true)
            fetch(`/rest/v2/accounting/to-be-paid-tasks`, {
                method: 'PUT',
                body: JSON.stringify({
                    taskIds: toBePaidTaskIds
                })
            })
            .then((response) => response.json())
            .then(({success, errors}) => {
                if (!success) {
                    toastr.error(errors.join(', '))
                } else {
                    handleSearch()
                }
            })
            .catch((error) => {
                toastr.error(error)
                setLoading(false)
            })
        }
    }

    const handleSearch = (params = {}) => {
        setLoading(true)
        params.supplierIds = Array.isArray(params.suppliers)
            ? params.suppliers.map((supplier) => supplier.value)
            : null
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== null && value !== '')
        )

        const urlSearchParams = new URLSearchParams(filteredParams)
        const queryString = urlSearchParams.toString()

        fetch(`/rest/v2/accounting/to-be-paid-tasks${queryString ? `?${queryString}` : ''}`)
        .then((response) => response.json())
        .then(({ data, success, errors }) => {
            if (!success) {
                toastr.error(errors.join(', '))
                return
            }
            setEntries(data)
            setLoading(false)
        })
        .catch((error) => toastr.error(error))
    }

    const handleOnTaskSelected = (id) => {
        if (selectedTaskIds.includes(id)) {
            setSelectedTaskIds((prev) => prev.filter((taskId) => taskId !== id))
        } else {
            setSelectedTaskIds((prev) => [...prev, id])
        }
    }

    const handleDownloadCSV = () => {
        if (!entries.length) {
            toastr.error("No entries to export.")
            return
        }

        const headers = Object.keys(entries[0]).filter(field => field !== "order_name") // Exclude order_name since it should not be in the export
        const csvContentEntries = !!selectedTaskIds.length
            ? entries.filter((task) => selectedTaskIds.includes(task.task_uid))
            : entries

            const processedEntries = csvContentEntries.map(entry => ({
                ...Object.fromEntries(
                    headers
                        .filter(field => field !== "task_uid" && field !== "order_name") // Exclude task_uid and order_name
                        .map(field => [field, entry[field] ?? ""])
                ),
                task_uid: `${entry.task_uid} - ${entry.order_name || ""}` // Add and format task_uid
            }))

        if (!processedEntries.length) {
            toastr.error("No matching tasks found for export.")
            return
        }

        const csvContent =
        [
            headers.join(','),
            ...processedEntries.map(row =>
                headers.map(header =>
                    `"${String(row[header]).replace(/"/g, '""')}"`
                ).join(',')
            )
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Fakturafil export from ${entries.length > 0 ? entries[0].date : 'data'}.csv`)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className="w-full h-full">
            <ReportHeader
                loading={loading}
                entries={entries}
                onDownloadCsv={handleDownloadCSV}
                onSearch={handleSearch}
                showFullTaskRow={showFullTaskRow}
                onShowFullTaskRowChange={() => setShowFullTaskRow(!showFullTaskRow)}
            />
            <ReportResults
                selectedTaskIds={selectedTaskIds}
                onTaskSelected={handleOnTaskSelected}
                loading={loading}
                entries={entries}
                onMarkAsPaid={handleMarkAsPaid}
                showFullTaskRow={showFullTaskRow}
            />
        </div>
    )
}

export default AccountingToBePaidTasksPage
